import React, { useEffect, useRef, useState } from "react";
import { Grid2, Paper, TextField, Typography } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";

/** Paper styling aligned with summary and vehicle cards — same background layer (background.paper) and border */
const tripReasonPaperSx = {
  mt: 1,
  p: 2,
  borderRadius: 2,
  border: (theme: Theme) => `1px solid ${theme.palette.divider}`,
  backgroundColor: (theme: Theme) => theme.palette.background.paper
};

export type FormVehicleTripReasonFieldProps = {
  valueRef: React.MutableRefObject<string>;
  initialValue: string;
  /** When true, shows error styling and optional helperText (e.g. required) */
  error?: boolean;
  /** Shown below the field when error is true */
  helperText?: string;
  /** Called when empty/non-empty state changes so parent can disable confirm without re-rendering on every keystroke */
  onValidityChange?: (isEmpty: boolean) => void;
};

/**
 * Isolated trip reason field so typing does not re-render the parent (avoids vehicle image flicker).
 * Owns local state and syncs value to valueRef for submit.
 */
function isEmptyValue(value: string): boolean {
  return value.trim() === "";
}

export function FormVehicleTripReasonField({
  valueRef,
  initialValue,
  error = false,
  helperText,
  onValidityChange
}: Readonly<FormVehicleTripReasonFieldProps>) {
  const { t } = useTranslation();
  const [value, setValue] = useState(initialValue);
  const lastEmptyRef = useRef<boolean>(isEmptyValue(initialValue));

  useEffect(() => {
    valueRef.current = initialValue;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync ref on mount only
  }, []);

  useEffect(() => {
    if (onValidityChange) {
      const empty = isEmptyValue(initialValue);
      lastEmptyRef.current = empty;
      onValidityChange(empty);
    }
    // Run on mount so parent knows initial validity
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    setValue(next);
    valueRef.current = next;
    if (onValidityChange) {
      const empty = isEmptyValue(next);
      if (empty !== lastEmptyRef.current) {
        lastEmptyRef.current = empty;
        onValidityChange(empty);
      }
    }
  };

  return (
    <Grid2 size={{ xs: 12 }}>
      <Paper
        elevation={0}
        sx={{ ...tripReasonPaperSx }}
        data-testid="form-confirm-trip-reason-section"
      >
        <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1.5 }}>
          {t("Trip Reason")}
        </Typography>
        <TextField
          data-testid="form-confirm-trip-reason"
          fullWidth
          variant="outlined"
          maxRows={3}
          multiline
          placeholder={t("_scheduleSelection_tripReasonPlaceholder")}
          value={value}
          onChange={handleChange}
          error={error}
          helperText={helperText}
          slotProps={{ input: { maxLength: 500 } }}
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: (theme: Theme) => theme.palette.background.paper
            }
          }}
        />
      </Paper>
    </Grid2>
  );
}
